import { useState } from 'react';

import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Sheet, SheetClose, SheetContent, SheetTrigger } from '@/components/ui/sheet';

import { SettingType } from '../types/setting-type';
import { SettingsNavigation } from './settings-navigation';

interface SettingsMobileMenuProps {
  availableSettings: SettingType[];
}

/**
 * Mobile-only settings menu component
 * Shows a "Settings menu" button that opens a drawer with navigation
 * Auto-closes when a navigation item is clicked
 */
export function SettingsMobileMenu({ availableSettings }: SettingsMobileMenuProps) {
  const { t } = useTranslation(['authenticated.settings', 'common']);
  const [open, setOpen] = useState(false);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline" className="w-full justify-start gap-2">
          <Menu className="h-4 w-4" />
          <span>{t('mobileMenu.trigger')}</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="w-[196px] gap-0 p-0 [&>button]:hidden">
        {/* Custom header with close button */}
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 className="text-lg font-semibold text-foreground">{t('title')}</h2>
          <SheetClose asChild>
            <Button variant="outline" size="icon" className="h-7 w-7 shrink-0">
              <X className="h-4 w-4" />
              <span className="sr-only">{t('common:close')}</span>
            </Button>
          </SheetClose>
        </div>

        {/* Navigation content - closes sheet on navigation */}
        <div
          className="overflow-y-auto p-1"
          onClick={event => {
            const target = event.target;
            if (target instanceof Element && target.closest('a')) {
              setOpen(false);
            }
          }}
        >
          <SettingsNavigation availableSettings={availableSettings} />
        </div>
      </SheetContent>
    </Sheet>
  );
}
